import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import SettingsInputHdmiOutlinedIcon from '@material-ui/icons/SettingsInputHdmiOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import { useGetList, useNotify } from 'react-admin';
import LinearProgress from '@material-ui/core/LinearProgress';
import SuggestionIntegration from './SuggestionIntegration';
import ConnectForm from './ConnectForm';
import ToggleForm from './ToggleForm';
import { apiPost, apiGet, apiPut } from '../brainApi';
import MoreInfoModal from './MoreInfoModal';

const useStyles = makeStyles((theme) => ({
  integrationWrapper: {
    marginTop: '15px',
  },
  integrationPaper: {
    padding: '10px',
    position: 'relative',
    minHeight: '250px',
    overflow: 'hidden',
    height: '90%',
  },
  integrationLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  integrationConnect: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    flexWrap: 'wrap',
    marginTop: 10,
    marginBottom: 10,
  },
  integrationLogoWrapper: {
    width: '130px',
    height: '130px',
    margin: '15px auto',
    display: 'flex',
    alignItems: 'center',
  },
  integrationLogo: {
    width: '100%',
  },
  integrationBadge: {
    width: 'auto',
    padding: '16px 70px',
    position: 'absolute',
    textAlign: 'center',
    color: '#b7b6b6',
    right: '-62px',
    top: '22px',
    backgroundColor: '#f9f9f9',
    boxShadow: '0px 1px 1px #efefef',
    transform: `rotate(45deg)`,
  },
  integrationHeader: {
    margin: '10px',
  },
  cardIndicator: {
    width: 25,
    height: 25,
    borderRadius: '50%',
  },
  cardInactive: {
    backgroundColor: '#fff',
    boxShadow: 'inset 0 0 1px 3px #CC0000',
  },
  cardActive: {
    backgroundColor: 'green',
  },
  grayText: {
    color: '#808080',
  },
  buttonRounded: {
    border: '1px solid #808080',
    borderRadius: '25px',
    minWidth: '100px',
    [theme.breakpoints.down('xs')]: {
      minWidth: 'auto',
    },
    padding: '5px 10px',
    marginBottom: '5px',
  },
  buttonConnect: {
    marginLeft: 10,
  },
  linearProgress: {
    marginTop: '100px',
    paddingTop: '2px',
    width: '70%',
    marginLeft: '15%',
  },
}));

const YourIntegration = ({ connection }) => {
  const {
    name,
    logo_url,
    more_info,
    integration_connection_id,
    is_active,
    summary,
  } = connection;

  const [activationModalIsOpen, setActivationModalIsOpen] = useState(false);
  const closeActivationModal = () => setActivationModalIsOpen(false);
  const [moreInfoModalIsOpen, setMoreInfoModalIsOpen] = useState(false);
  const closeMoreInfoModal = () => setMoreInfoModalIsOpen(false);
  const classes = useStyles();

  const setSquareLocation = async function(location_id) {
    if (location_id !== savedSquareLocation) {
      setSavedSquareLocation(location_id);
      const setPreferredSquareLocation = await apiPut(
        '/integration/square/location',
        { location_id },
      );
    }
  };

  const [squareLocationsList, setSquareLocationsList] = useState([]);
  const [savedSquareLocation, setSavedSquareLocation] = useState('');

  useEffect(() => {
    const getSquareLocations = async function() {
      const locations = await apiGet('/integration/square/locations');
      if (Array.isArray(locations)) {
        setSquareLocationsList(locations);
        const preferred_location = locations.filter(
          (location) => location.preferred,
        )[0];
        if (preferred_location) {
          setSavedSquareLocation(preferred_location.id);
        }
      }
      return locations;
    };

    getSquareLocations();
  }, []);

  return (
    <Paper
      variant="outlined"
      elevation={3}
      className={classes.integrationPaper}
    >
      {activationModalIsOpen && (
        <ToggleForm
          name={name}
          integrationConnectionId={integration_connection_id}
          closeModal={closeActivationModal}
          isActive={is_active}
        />
      )}
      {moreInfoModalIsOpen && (
        <MoreInfoModal
          integration={{ name, more_info }}
          closeModal={closeMoreInfoModal}
        />
      )}
      <div className={classes.integrationLabel}>
        <Typography variant="h6" gutterBottom>
          {name}
        </Typography>
        <Tooltip title={is_active ? 'Active' : 'Inactive'} placement="top">
          <span
            className={[
              `${classes.cardIndicator} ${
                is_active ? `${classes.cardActive}` : `${classes.cardInactive}`
              }`,
            ]}
            onClick={() => setActivationModalIsOpen(true)}
          />
        </Tooltip>
      </div>
      <div className={classes.integrationLogoWrapper}>
        <img src={logo_url} alt={name} className={classes.integrationLogo} />
      </div>
      <div className={classes.integrationConnect}>
        <Button
          onClick={() => setMoreInfoModalIsOpen(true)}
          startIcon={<InfoOutlinedIcon />}
          className={`${classes.buttonRounded} ${classes.grayText}`}
        >
          Info
        </Button>
        <Button
          className={`${classes.buttonRounded} ${classes.buttonConnect}`}
          onClick={() => setActivationModalIsOpen(true)}
          variant="contained"
          color={is_active ? '' : 'primary'}
        >
          {is_active ? 'Deactivate' : 'Activate'}
        </Button>
      </div>
      <div>
        {connection.name === 'Square' && (
          <>
            <span className={classes.integrationConnect} style={{ margin: 0 }}>
              <Typography>Location ID:</Typography>
            </span>
            <span className={classes.integrationConnect} style={{ margin: 0 }}>
              <Select
                size="large"
                value={savedSquareLocation || null}
                onChange={(e) => setSquareLocation(e.target.value)}
              >
                {squareLocationsList &&
                  squareLocationsList.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {`${name} (${id})`}
                    </MenuItem>
                  ))}
              </Select>
            </span>
          </>
        )}
      </div>
      <div className={classes.integrationConnect}>
        <span>{summary}</span>
      </div>
    </Paper>
  );
};

const AvailableIntegration = ({ integration }) => {
  const { name, logo_url, requires_oauth, integration_id } = integration;
  const [connectModalIsOpen, setConnectModalIsOpen] = useState(false);
  const closeConnectModal = () => setConnectModalIsOpen(false);
  const [moreInfoModalIsOpen, setMoreInfoModalIsOpen] = useState(false);
  const closeMoreInfoModal = () => setMoreInfoModalIsOpen(false);
  const classes = useStyles();
  const notify = useNotify();

  const onClickConnect = () => {
    if (requires_oauth) {
      // By default Safari blocks window.open() when it's called in an async method (or promise)
      // so we first need to open the new tab outside of the promise with no uri and then we'll
      // set the location in the promise
      const integrationWindow = window.open('', '_blank');
      apiPost(`/integration/${integration_id}/initiate-oauth`)
        .then(({ authorization_uri }) => {
          if (authorization_uri) {
            integrationWindow.location = authorization_uri;
          } else {
            integrationWindow.close();
            notify('Sorry, unable to connect.', 'error');
          }
        })
        .catch((e) => {
          integrationWindow.close();
          console.error(e);
          notify('Sorry, unable to connect.', 'error');
        });
    } else {
      setConnectModalIsOpen(true);
    }
  };

  return (
    <Paper
      variant="outlined"
      elevation={3}
      className={classes.integrationPaper}
    >
      {connectModalIsOpen && (
        <ConnectForm integration={integration} closeModal={closeConnectModal} />
      )}
      {moreInfoModalIsOpen && (
        <MoreInfoModal
          integration={integration}
          closeModal={closeMoreInfoModal}
        />
      )}
      <div className={classes.integrationLabel}>
        <Typography variant="h6" gutterBottom>
          {name}
        </Typography>
      </div>
      <div className={classes.integrationLogoWrapper}>
        <img src={logo_url} alt={name} className={classes.integrationLogo} />
      </div>

      <div className={classes.integrationConnect}>
        <Button
          onClick={() => setMoreInfoModalIsOpen(true)}
          startIcon={<InfoOutlinedIcon />}
          className={`${classes.buttonRounded} ${classes.grayText}`}
        >
          Info
        </Button>
        <Button
          onClick={onClickConnect}
          startIcon={<SettingsInputHdmiOutlinedIcon />}
          className={`${classes.buttonRounded} ${classes.buttonConnect}`}
          variant="contained"
          color="primary"
        >
          Connect
        </Button>
      </div>
    </Paper>
  );
};

const Integrations = () => {
  const classes = useStyles();
  const { data, loading } = useGetList(
    'integration',
    { page: 1, perPage: 2000 },
    { field: 'integration_id', order: 'ASC' },
    {},
  );

  if (loading) {
    return (
      <div className={classes.integrationWrapper}>
        <LinearProgress className={classes.linearProgress} />
      </div>
    );
  }

  const allIntegrations = data ? Object.values(data) : [];

  const yourIntegrations = allIntegrations.filter(
    (i) => i.connections && i.connections.length,
  );
  // Collect an array of all the connections from each integration
  const yourIntegrationConnections = yourIntegrations
    .map((i) => i.connections.map((c) => ({ ...i, ...c })))
    .flat(1);
  const availableIntegrations = allIntegrations.filter(
    (i) =>
      (!i.connections ||
        !i.connections.length ||
        i.can_support_multiple_connections) &&
      i.is_accepting_connections,
  );

  return (
    <div className={classes.integrationWrapper}>
      {!!yourIntegrationConnections && !!yourIntegrationConnections.length && (
        <Typography
          variant="h4"
          gutterBottom
          className={classes.integrationHeader}
        >
          Your Integrations
        </Typography>
      )}
      <Grid container spacing={3} xs={12}>
        {yourIntegrationConnections?.map((c) => (
          <Grid
            key={c.integration_connection_id}
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            alignItems="stretch"
          >
            <YourIntegration connection={c} />
          </Grid>
        ))}
      </Grid>
      {!!availableIntegrations && !!availableIntegrations.length && (
        <Typography
          variant="h4"
          gutterBottom
          className={classes.integrationHeader}
        >
          Available Integrations
        </Typography>
      )}
      <Grid container spacing={3} xs={12}>
        {availableIntegrations?.map((integration) => (
          <Grid
            key={integration.id}
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            alignItems="stretch"
          >
            <AvailableIntegration integration={integration} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3} xs={12}>
        <Grid item xs={12} sm={6} md={4} lg={3} alignItems="stretch">
          <SuggestionIntegration />
        </Grid>
      </Grid>
    </div>
  );
};

export default Integrations;
